import * as React from "react";
import { useMappedState } from "redux-react-hook";
import styled, {
  css,
  useTheme
} from "styled-components";
import { toast } from "react-toastify";
import _ from "lodash";
import * as queryString from "query-string";
import { useRouteMatch } from "react-router-dom";
import moment from "moment-timezone";

import { breakpoint } from "src/library/utils/styled-media-queries";
import { Button } from "src/library/components/Button";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { TabbedNavigation } from "src/library/components/TabbedNavigation";
import { OrderActionListWithPONumber } from "src/core/shared/components/PurchaseOrderNumber";
import OrderErrorsAndNotes from "src/core/orders/components/OrderErrorsAndNotes";
import OrderInfoSidebar from "src/core/orders/components/OrderInfoSidebar";
import OrderLeadTimeSplit from "src/core/orders/components/OrderLeadTimeSplit";
import OrderProductLine from "src/core/orders/components/OrderProductLine";
import { ConvertToAdhocOrderModal } from "src/core/orders/components/Modals";
import { OrderActionsList } from "src/core/orders/components/OrderActionsList";
import { ErrorNotice } from "src/core/shared/components/ErrorNotice";
import {
  InfoBarWrapper,
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import type { IStore } from "src/core/shared/store";
import { useOrder } from "src/core/orders/hooks";
import {
  useOrderCutoffTime,
  useOrderLineError
} from "src/core/orders/hooks/validation";
import { useCurrentOrderValidation } from "src/context/order-validation-context";
import { PageProps } from "src/pages/Router/routes";
import {
  IOrder,
  IOrderStatus,
  OrderMode
} from "src/core/orders/api/orders.api";
import {
  OrderPaymentErrorNoticeModal,
  TransactionsModal
} from "src/modules/payment/components";
import { TransactionStatus } from "src/modules/payment/api/transactions.api";
import { OrderAuditLogs } from "src/core/orders/components/OrderAuditLogs/OrderAuditLogs";
import { OrderSupportTicketsTab } from "src/modules/support/components/OrderSupportTicketsTab";
import { RapidOrderEntry } from "src/core/products/components/RapidOrderEntry/RapidOrderEntry";
import { useIsMobile } from "src/library/hooks";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import CancelOrderModal from "src/core/orders/components/Modals/CancelOrderModal";
import { DeliveryChargeLine } from "src/core/orders/components/DeliveryChargeLine";
import { useCanPayForOrder } from "src/modules/payment/hooks/useCanPayForOrder";
import { validateAgainstMaxPackSizeConstraints } from "src/core/orders/utils/orders";
import RelatedInvoicesTab from "src/core/accounting/components/RelatedInvoicesTab";

interface ProductsPageProps {
  orderId: string;
}

/**
 * Main "Products" View
 *
 * @returns
 */
const ViewOrderPage: React.ReactFCWithChildren<PageProps<ProductsPageProps>> = props => {
  const { terminology, location } = props;
  const match = useRouteMatch<ProductsPageProps>();
  const { canBeSubmitted, canBeCancelled } = useCurrentOrderValidation();
  const orderCutOffTime = useOrderCutoffTime();
  const orderId = React.useMemo(() => parseInt(match.params.orderId, 0), [ match ]);
  const getOrderLineError = useOrderLineError();

  const {
    erp,
    currentRole,
    orderStatuses,
    installedModules,
    supportSetting,
    masqueradeMode,
    displaySettings,
    packSizeConstraints,
    orderVariants
  } = useMappedState(({ global }: IStore) => ({
    currentRole: global.role,
    orderStatuses: (global.settings && global.settings.orderStatuses) || [] as IOrderStatus[],
    installedModules: global.settings ? global.settings.installedModules : [],
    erp: global.settings && global.settings.erp.name,
    supportSetting: global.settings?.supportSettings,
    masqueradeMode: global.masqueradeMode,
    displaySettings: global.settings?.displaySettings,
    packSizeConstraints: global?.settings?.packSizeConstraints,
    orderVariants: global?.settings?.orderVariants || []
  }));

  const [ hasSetLocationProps, setHasSetLocationProps ] = React.useState(false);
  const [ showConvertToAdhocModal, setShowConvertToAdhocModal ] = React.useState(false);
  const [ showTransactionsModal, setShowTransactionsModal ] = React.useState(false);
  const [ cancelOrderModal, setCancelOrderModal ] = React.useState<number | null>(null);
  const checkUserPermission = useCheckUserPermission();
  const isMobile = useIsMobile();
  const theme = useTheme();

  const [
    {
      isLoading: isLoadingOrder, currentOrder, drop, cancelOrderResponse
    },
    {
      setOrderMode, submitOrder, cancelOrder
    }
  ] = useOrder(orderId, null, true);

  const canPayNow = useCanPayForOrder(currentOrder.data);
  let timeSinceCutOff: number | null = null;

  if (currentOrder.data) {
    const cutOffTime = orderCutOffTime(currentOrder.data);
    const currentTime = moment.utc();

    if (cutOffTime && currentTime.isAfter(cutOffTime)) {
      timeSinceCutOff = currentTime.diff(cutOffTime, "hours");
    }
  }

  /**
   * get any constraint warnings if any.
   */
  const packSizeConstraintWarningMessage = React.useMemo(() => {
    if (currentOrder.data) {
      return validateAgainstMaxPackSizeConstraints(currentOrder.data, orderVariants, packSizeConstraints, terminology, "warn");
    } else return null;
  }, [
    currentOrder.data,
    packSizeConstraints,
    terminology,
    orderVariants
  ]);

  /**
   * Sets the primary action button
   */
  const primaryAction = React.useCallback(() => {
    if (currentOrder.data && drop && orderStatuses.length) {
      const payableStatuses = _.map(_.filter(orderStatuses, ordStatus => {
        return ordStatus.settings.group === "PROVISIONAL";
      }), "id");

      const orderIsPayable = payableStatuses.includes(currentOrder.data.orderStatusId) && !masqueradeMode?.active;

      if (currentOrder.mode === "view" && orderIsPayable) {
        const latestTransaction = currentOrder.data.transactions && currentOrder.data.transactions.length > 0 ? currentOrder.data.transactions[ 0 ] : null;

        if (latestTransaction?.status === TransactionStatus.REQUIRES_PAYMENT_METHOD || latestTransaction?.status === TransactionStatus.REQUIRES_ACTION) {
          return (
            <Button
              $buttonType="primary"
              onClick={() => props.history.push(`/orders/${currentOrder.data?.id}/checkout`)}
              disabled={isLoadingOrder}
            >
              Complete payment
            </Button>
          );
        }

        if (canPayNow) {
          return (
            <Button
              $buttonType="secondary"
              onClick={() => props.history.push(`/orders/${currentOrder.data?.id}/checkout`)}
              disabled={isLoadingOrder}
            >
              Pay now
            </Button>
          );
        }
      }
    }

    return null;
  }, [
    currentOrder.data,
    currentOrder.mode,
    drop,
    orderStatuses,
    canPayNow,
    isLoadingOrder,
    props.history,
    masqueradeMode?.active
  ]);

  /**
   * Sets the secondary action button
   */
  const secondaryOrder = React.useCallback(() => {
    if (currentOrder.data) {
      if (currentOrder.mode === "edit") {
        return (
          <OrderActionListWithPONumber
            terminology={terminology}
            setShowConvertToAdhocModal={setShowConvertToAdhocModal}
          />
        );
      } else if (currentOrder.mode === "view") {
        if (drop) {
          if (currentOrder.data && currentRole && drop && erp) {
            return (
              <OrderActionsList
                terminology={terminology}
                erp={erp}
                role={currentRole}
              />
            );
          }
        }
      }
    }

    return null;
  }, [
    currentOrder,
    terminology,
    drop,
    currentRole,
    erp
  ]);

  React.useEffect(() => {
    if (currentOrder === null || currentOrder.data?.id !== orderId) {
      setHasSetLocationProps(false);
    }

    if (currentOrder.data) {
      if (location.state && location.state.edit) {
        if (currentOrder.data.id === orderId) {
          if (!hasSetLocationProps) {
            const setModeFromLocation = setOrderMode("edit");

            if (setModeFromLocation) {
              setHasSetLocationProps(true);
            }
          }
        }
      }
    }
  }, [
    currentOrder,
    hasSetLocationProps,
    location.state,
    orderId,
    setOrderMode
  ]);

  // determine on page load if we want to open print order pdf on load
  const printOrderPDFOnLoad: boolean = React.useMemo(() => {
    if (location) {
      const params = queryString.parse(location.search);

      return params.printOrder === "true";
    }

    return false;
  }, [ location ]);

  const tabs = React.useMemo(() => {
    const orderTabs = [
      {
        title: currentOrder.data?.orderStatus?.code === "invoiced" && !installedModules.includes("accounting") ?
          "Invoice" :
          _.capitalize(props.terminology.product.plural.text),
        pad: {
          top: 0,
          bottom: 0,
          left: 0,
          right: 0
        },
        content: () => (
          <>
            {!!currentOrder.data && (
              <div className="order-tab">
                {currentOrder.data.deliveryCharge && (
                  <DeliveryChargeLine
                    deliveryCharge={currentOrder.data.deliveryCharge}
                    currency={drop?.priceCode?.currency}
                  />
                )}

                <OrderLeadTimeSplit
                  openPrintOnLoad={printOrderPDFOnLoad}
                  drop={drop}
                  theme={theme}
                  currentOrderMode={currentOrder.mode}
                  order={currentOrder.data}
                  installedModules={installedModules}
                  OrderProductLine={orderLine => {
                    const currentOrd = currentOrder.data as IOrder;

                    if (drop) {
                      // TODO: is this appropriate here? Assuming we can focus on errors for the active product and ignore substituted one
                      orderLine.errors = getOrderLineError(orderLine, currentOrd.orderDeliveryDate, currentOrd.orderStatus);

                      return (
                        <OrderProductLine
                          installedModules={installedModules}
                          order={currentOrd}
                          allowedDays={currentOrder.allowedDays}
                          drop={drop}
                          key={orderLine.productId}
                          orderMode={currentOrder.mode}
                          orderLine={orderLine}
                        />
                      );
                    }

                    return null;
                  }}
                />
              </div>
            )}
          </>
        )
      },
      {
        title: `Notes (${currentOrder.data ? currentOrder.data.orderNotes?.length || 0 : ""})`,
        content: () => (
          <>
            {!!currentOrder.data && (
              <OrderErrorsAndNotes
                orderId={currentOrder.data.id}
                terminology={terminology}
              />
            )}
          </>
        )
      }
    ];

    if (installedModules.includes("support") && supportSetting?.supportTickets.enabled) {
      orderTabs.push({
        title: "Support",
        content: () => (
          <>
            {currentOrder.data && (<OrderSupportTicketsTab orderId={currentOrder.data.id} />)}
          </>
        )
      });
    }

    if (currentRole?.type === "admin") {
      orderTabs.push({
        title: "Change log",
        content: () => (
          <>
            {currentOrder.data && (<OrderAuditLogs orderId={currentOrder.data.id} />)}
          </>
        )
      });
    }

    // Add Invoice tab when accounting module is installed and order has invoices
    // Support both new many-to-many (invoices array) and legacy single invoice
    const orderInvoices = currentOrder.data?.invoices?.length ? currentOrder.data.invoices : [];

    if (installedModules.includes("accounting") && orderInvoices.length > 0) {
      orderTabs.push({
        title: orderInvoices.length > 1 ? "Invoices" : "Invoice",
        pad: {
          top: 0,
          bottom: 0,
          left: 0,
          right: 0
        },
        content: () => (
          <RelatedInvoicesTab
            orderId={orderId}
          />
        )
      });
    }

    return orderTabs;
  }, [
    currentOrder.allowedDays,
    currentOrder.data,
    currentOrder.mode,
    currentRole?.type,
    drop,
    getOrderLineError,
    installedModules,
    orderId,
    printOrderPDFOnLoad,
    props.terminology.product.plural.text,
    supportSetting?.supportTickets.enabled,
    terminology,
    theme
  ]);

  const orderWarning = React.useMemo(() => {
    if (currentOrder.mode === "view") {
      if (timeSinceCutOff && displaySettings?.order?.hoursAfterCutOff && timeSinceCutOff < displaySettings?.order?.hoursAfterCutOff) {
        return displaySettings?.order?.cutoffMessage;
      } else if (currentOrder.data?.orderStatus?.settings.group === "CANCELLED" && currentOrder.data.scheduleId) {
        return `This order was part of a ${terminology.schedule.singular.text} but was manually cancelled. If you would like an order for this delivery date please manually place the order`;
      }
    }

    return null;
  }, [
    currentOrder.data?.orderStatus?.settings.group,
    currentOrder.data?.scheduleId,
    currentOrder.mode,
    displaySettings?.order?.cutoffMessage,
    displaySettings?.order?.hoursAfterCutOff,
    terminology.schedule.singular.text,
    timeSinceCutOff
  ]);

  return (
    <Layout
      {...props}
      $noPadding={isMobile}
    >
      <ViewOrderPageLayout currentOrderMode={currentOrder.mode}>
        <>
          <MainContentWrapper fill>
            {isLoadingOrder ? (
              <LoadingSpinner type="circular" />
            ) : (
              !!currentOrder.data &&
            !!currentRole && (
              <>
                {currentOrder.mode === "edit" && typeof canBeSubmitted === "string" && (
                  <ErrorNotice
                    type="error"
                    message={canBeSubmitted}
                  />
                )}

                {currentOrder.mode === "edit" && typeof packSizeConstraintWarningMessage === "string" && (
                  <ErrorNotice
                    type="warn"
                    message={packSizeConstraintWarningMessage}
                  />
                )}

                {orderWarning && (
                  <ErrorNotice
                    type="warn"
                    message={orderWarning}
                  />
                )}

                {currentOrder.mode === "edit" && (
                  <RapidOrderEntry />
                )}

                <TabbedNavigation
                  defaultTab="order"
                  className="tab-navigator"
                  tabs={tabs}
                />
              </>
              )
            )}
          </MainContentWrapper>

          {drop && (
            <InfoBarWrapper>
              <OrderInfoSidebar
                viewMode={currentOrder.mode}
                drop={drop}
                orderMode={currentOrder.mode}
                primaryAction={primaryAction()}
                secondaryAction={secondaryOrder()}
                terminology={terminology}
                isLoading={isLoadingOrder}
                data={currentOrder.data}
                onTransactionClick={() => setShowTransactionsModal(true)}
              />

              {currentOrder.mode === "edit" && isMobile && (
                <Button
                  $buttonType="plain"
                  onClick={() => {
                    if (checkUserPermission("delete-order")) {
                      if (match.params.orderId && currentOrder.data && canBeCancelled === null) {
                        setCancelOrderModal((currentOrder.data as IOrder).id);
                      }
                    } else {
                      toast.error("You do not have permission to delete this order.");
                    }
                  }}
                >
                  Or, cancel this order
                </Button>
              )}
            </InfoBarWrapper>
          )}

          {currentOrder.data && (
            <ConvertToAdhocOrderModal
              terminology={terminology}
              show={showConvertToAdhocModal}
              onConfirm={submitOrder}
              handleDismiss={() => setShowConvertToAdhocModal(false)}
              order={currentOrder.data}
            />
          )}

          {currentOrder.data && currentOrder.data.id === orderId && !isLoadingOrder && (
            <OrderPaymentErrorNoticeModal
              order={currentOrder.data}
              terminology={terminology}
            />
          )}

          <TransactionsModal
            orderId={orderId}
            open={showTransactionsModal}
            onDismiss={() => setShowTransactionsModal(false)}
            transactions={currentOrder.data?.transactions}
          />

          {currentOrder.data && (
            <CancelOrderModal
              order={currentOrder.data as IOrder}
              show={cancelOrderModal ? true : false}
              handleDismiss={() => setCancelOrderModal(null)}
              loading={cancelOrderResponse.pending}
              onConfirm= {() => {
                cancelOrder(currentOrder?.data?.id as number);
              }}
              terminology={terminology}
              success={cancelOrderResponse.data}
              failure={cancelOrderResponse?.error?.message}
            />
          )}
        </>
      </ViewOrderPageLayout>
    </Layout>
  );
};

const ViewOrderPageLayout = styled.div<{currentOrderMode?: OrderMode}>`
  ${({ theme, currentOrderMode }) => css`
    width: 100%;
    height: 100%;
    display: contents;
    

    .main-content {
      order: 1;
    }


    .info-bar-wrapper {
      order: 2;
    }

    ${currentOrderMode === "edit" && css`
      .input-wrapper input {
        border: 1px solid ${theme.colors.primaryDark};
        border-right: none;
      }

      .actions-wrapper {
        background: inherit;
        border: none;
      }

      .button-wrapper {
        width: 100%;
        justify-content: center;
      }

      .main-content {
        order: 2;
      }
  
      .info-bar-wrapper {
        order: 1;
      }
    `}

    ${breakpoint("lg", theme)} {
      display: flex;

      .main-content {
        order: 1;
      }
  
      .info-bar-wrapper {
        order: 2;
      }
    }
  `}
`;

export default ViewOrderPage;
